import algosdk from 'algosdk';
import Application from './Application';
import { PersistGate } from 'redux-persist/lib/integration/react';
import '/node_modules/react-grid-layout/css/styles.css';
import '/node_modules/react-resizable/css/styles.css';
import {
  ALGOD_TOKEN,
  ALGO_SERVER,
  ALGO_SERVER_PORT,
  SOCKET_URL,
  API_HOST,
  ALGO_NETWORK,
} from './constants/constants';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { store, persistor } from './redux/_store/configureStore';
import { Client } from '@libs/shared/sdk';
import NoAccess from './components/pages/Exchange/NoAccess/NoAccess';
import {
  clearUseWalletState,
  loginTokenService,
  secureStorage,
} from './services/localStorage.service';
import { ModalApp } from './components/common';
import { saveSDKClient } from './redux/_actions/sdkClient.action';
import { saveRTKSDKClient } from './redux/_actions/rtkSdkClient.action';
import { useAppDispatch, useAppSelector, useIsLoggedSelector } from './services/hooks.service';
import { saveMainWallet, saveUserKey } from './redux/_actions/user.action';
import { checkIfShouldInheritWallet, ParentAppService } from './services/parrentApp.service';
import LoaderComponent from './components/common/LoaderComponent/LoaderComponent';
import { useSignMessage } from './wallets/useSignMessage';
import { BLOCKCHAINS, PROVIDERS, getBlockchainByChainId, getEvmChainId, isEvmChain } from './wallets/providers';
import { setChain, setIsConnected, setNetworkConfig } from './redux/_actions/network.action';
import { useActiveAccount } from './wallets/useActiveAccount';
import { convertJsonToArray, equalsIgnoreCase, isKeyExpired } from './_utils';
import { SolanaConnector } from './wallets/solana/solana.service';
import { EVMConnector } from './wallets/evm/evm.service';
import { saveSettings } from './redux/_actions/settings.action';
import { saveListOfPairs } from './redux/_actions/exchange.action';
import { IPairDto, ITradingKey, SettingIds, SettingsInit } from './interfaces';
import RTKSDKAdaptor, { initialPairListState, useGetPairListQuery, useGetSettingsQuery } from '@ultrade/react-sdk';
import { io } from 'socket.io-client';

if (window.Cypress) {
  window.store = store;
}

export const algodClient = new algosdk.Algodv2(
  ALGOD_TOKEN,
  ALGO_SERVER,
  ALGO_SERVER_PORT
);

const rtkAlgodClient = new algosdk.Algodv2(
  ALGOD_TOKEN,
  ALGO_SERVER,
  ALGO_SERVER_PORT
);

const sdkClient = new Client({
  network:
    ALGO_NETWORK === 'MainNet'
      ? 'mainnet'
      : ALGO_NETWORK === 'TestNet'
      ? 'testnet'
      : 'local',
  apiUrl: API_HOST,
  algoSdkClient: algodClient,
  websocketUrl: SOCKET_URL,
  socketIO: io,
});


const rtkSdkClient = RTKSDKAdaptor.create({
  network:
    ALGO_NETWORK === 'MainNet'
      ? 'mainnet'
      : ALGO_NETWORK === 'TestNet'
      ? 'testnet'
      : 'local',
  apiUrl: API_HOST,
  algoSdkClient: rtkAlgodClient,
  websocketUrl: SOCKET_URL,
});

const isWalletInheritance = checkIfShouldInheritWallet();
if (isWalletInheritance) {
  window.isWalletInheritance = isWalletInheritance;
  clearUseWalletState();
}

export const parentAppService = ParentAppService.getInstance();
const evmConnector = EVMConnector.getInstance();
const solanaConnector = SolanaConnector.getInstance();

const App = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { signMessage, signMessageByToken } = useSignMessage();
  const isLogged = useIsLoggedSelector();
  const activeWallet = useActiveAccount();

  const [showNoAccsessPage, setShowNoAccsessPage] = useState<boolean | null>(null);
  const [warningModal, setWarningModal] = useState<boolean>(false);

  const [geoblockModal, setGeoblockModal] = useState<boolean>(false);
  const [isLogging, setIsLogging] = useState<boolean>(true);
  const isFetchingSettingsRef = useRef(false);
  const hasFetchedSettingsRef = useRef(false);

  const sdkClientInstance = useAppSelector((state) => state.sdkClient.instance);
  const rtkSdkClientInstance = useAppSelector((state) => state.rtkSdkClient.instance);
  const network = useAppSelector(state => state.network);
  const appTitle = useAppSelector((state) => state.settings.appTitle);
  const mainAddress = useAppSelector((state) => state.user.mainAddress);
  const mainWallet = loginTokenService.getMainWallet();
  const userKey = useAppSelector((state) => state.user.userLockalKey);
  const selectedPairKey = useAppSelector((state) => state?.user.selectedPair?.pair_key);
  const selectedPairId = useAppSelector((state) => state?.user.selectedPair?.id);
  
  const { data: settings, isFetching: isFetchingSettings } = useGetSettingsQuery();
  const { data: { listOfPairs } = initialPairListState, isFetching: isFetchingPairList } = useGetPairListQuery({selectedPairId}, {
    skip: !settings,
  });

  useEffect(() => {
    // console.log('NETWORK', network)
    if(network.chain === BLOCKCHAINS.SOLANA) {
      dispatch(setIsConnected(false));
      solanaConnector.reconnect(network.chain, network.provider)
      .then(() => {dispatch(setIsConnected(true))});
    } else if(isEvmChain(network.chain)) {
      dispatch(setIsConnected(false));
      evmConnector.reconnect(network.chain, network.provider)
      .then((chain) => {
        // console.log("reconnect chainId", chain, evmConnector.chainId)
        dispatch(setIsConnected(true));
        dispatch(setChain(getBlockchainByChainId(evmConnector.chainId)));
      })
      .catch(err => console.log("reconnect error", err));
    }

    // const remove = syncEvmEverySecond();
    // return remove;
  }, []);

  //sync evm chain with wallet chain<<
  useEffect(() => {
    // console.log('removeListener', { network: {...network}, activeWallet })
    const removeListener = evmConnector.onAccountChange(
      ({address, chainId, isConnected}) => {
        // console.log('evmConnector.onAccountChange => cb', {address, chainId, isConnected, network: {...network}, isEvm: isEvmChain(network.chain), activeWallet})
        const newState = {
          chain: getBlockchainByChainId(chainId),
          evmChainId: chainId,
          isConnected,
          provider: PROVIDERS.METAMASK,
        };
        if (activeWallet && isEvmChain(network.chain) && newState.chain && newState.evmChainId !== getEvmChainId(network.chain)) {
          dispatch(setNetworkConfig(newState));
        }
      }
    )
    return removeListener;
  }, [network.chain, activeWallet, dispatch]);
  //sync evm chain with wallet chain^^

  useEffect(() => {
    sdkClient.setSigner({
      signMessage,
      signMessageByToken,
    });
    rtkSdkClient.setSigner({
      signMessage,
      signMessageByToken,
    });
    const mainWallet = loginTokenService.getMainWallet();
    if (mainWallet) {
      dispatch(saveMainWallet(mainWallet));
    }
  }, [activeWallet]);

  useEffect(() => {
    if (!mainWallet) return;
    const existingTradingKey = rtkSdkClient.mainWallet?.tradingKey || userKey?.address;
    rtkSdkClient.mainWallet = mainWallet;
    if (existingTradingKey) {
      rtkSdkClient.mainWallet.tradingKey = existingTradingKey;
    }
  }, [mainWallet, userKey]);

  useEffect(() => {
    const data = secureStorage.getKeyFromLocalStorage();
    if (data) {
      if (!isKeyExpired(data.expiredAt)) {
        sdkClient.getTradingKeys()
        .then((keys: ITradingKey[]) => {
          if (Array.isArray(keys) && keys.some(el => equalsIgnoreCase(el.address, data.address))) {
            dispatch(saveUserKey({address: data.address, expiredAt: data.expiredAt}));
            sdkClient.mainWallet.tradingKey = data.address;
            rtkSdkClient.mainWallet.tradingKey = data.address;
          } else {
            secureStorage.clearKeyState();
            dispatch(saveUserKey(null));
            sdkClient.mainWallet.tradingKey = null;
            rtkSdkClient.mainWallet.tradingKey = null;
          }
        })
        .catch(error => console.log("ERROR on get Keys", error))
      } else {
        if (data) dispatch(saveUserKey({address: data.address, expiredAt: data.expiredAt}));
            sdkClient.mainWallet.tradingKey = null;
            rtkSdkClient.mainWallet.tradingKey = null;
      }
    }
  }, [isLogged]);

  if (sdkClientInstance === null) {
    dispatch(saveSDKClient(sdkClient));
  }

  if (rtkSdkClientInstance === null) {
    dispatch(saveRTKSDKClient(rtkSdkClient));
  }

  const getPairList = useCallback(async (settings: SettingsInit) => {
    const pinnedPairsArray = convertJsonToArray(settings[SettingIds.PINNED_PAIRS]);

    try {
      if (isFetchingPairList) return;
      // if (!response || !response.length) {
      //   return;
      // }
      // let availablePairs = notRestrictedPairs(response, currentCountry);

      let selectedId = selectedPairId;
      if (!selectedId && pinnedPairsArray.length) {
        const selectedPair = listOfPairs.find(pair => equalsIgnoreCase(pair.id, pinnedPairsArray[0]));
        if (selectedPair) selectedId = pinnedPairsArray[0];
      }

      const selectedMarket = new URLSearchParams(window.location.search).get('selectedMarket');
      if (selectedMarket) {
        const selectedPair = listOfPairs.find(pair => equalsIgnoreCase(pair.pair_key, selectedMarket));
        if (selectedPair) selectedId = selectedPair.id;
      }

      dispatch(saveListOfPairs(listOfPairs as unknown as IPairDto[], selectedId));
    } catch (error) { }
  }, [dispatch, listOfPairs, selectedPairId, isFetchingPairList]);

  const getCompanySettings = useCallback(async () => {
    try {
      if (isFetchingSettings) return;

      const {unprocessedSettings, ...apiSettings} = settings;
      const isEnabled = apiSettings[SettingIds.ENABLED] !== 'false';

      if (apiSettings.companyId) {
        sdkClient.companyId = apiSettings.companyId;
        rtkSdkClient.companyId = apiSettings.companyId;
      }
      if (
        apiSettings[SettingIds.GEOBLOCK] &&
        apiSettings.currentCountry &&
        JSON.parse(apiSettings[SettingIds.GEOBLOCK]).find(
          (el) => equalsIgnoreCase(el, apiSettings.currentCountry)
        )
      ) {
        setGeoblockModal(true);
      }
      setShowNoAccsessPage(!isEnabled);

      dispatch(saveSettings(unprocessedSettings));
      setIsLogging(false);
      hasFetchedSettingsRef.current = true;
      getPairList(unprocessedSettings);
    } catch (err) {
      setShowNoAccsessPage(true);
      setIsLogging(false);
    }
  }, [dispatch, getPairList, isFetchingSettings, settings]);


  useEffect(() => {
    getCompanySettings()
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && hasFetchedSettingsRef.current) {
        getCompanySettings();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [getCompanySettings]);

  useEffect(() => {
    const isMobile =
      /Mobile|webOS|BlackBerry|IEMobile|MeeGo|mini|Fennec|Windows Phone|Android|iP(ad|od|hone)/i.test(
        navigator.userAgent
      );
    const isFirefox = navigator.userAgent.includes('Firefox');
    if (isFirefox && isMobile) setWarningModal(true);
  }, []);

  return (
    <PersistGate loading={null} persistor={persistor}>
      <LoaderComponent />
      { isLogging ? <></>
        : showNoAccsessPage ? <NoAccess /> : <Application /> }

      <ModalApp show={warningModal} onHide={() => setWarningModal(false)}>
        <p> {t('error_msg.mobile_firefox_text', {app: appTitle})} </p>
        <button
          className="warning_btn"
          onClick={() => setWarningModal(false)}
        >
          {t('comman_word.go')}
        </button>
      </ModalApp>

      <ModalApp show={geoblockModal} width={400}>
        <p className="modal_info_center"> {t('error_msg.geoblocking_error')} </p>
      </ModalApp>
    </PersistGate>
  );
};

export default App;
